// view.js
import "./view.scss";



/**
 * Apply background color from .BackgroundColor element to .swiper
 * Searches up the DOM tree to find the nearest .BackgroundColor element
 */
function applySwiperBackgroundColor(container, swiperNode) {
  // The .BackgroundColor is a sibling of the post-slider in the parent's .inner-blocks-container
  // Structure: section > .container > .inner-blocks-container > [.BackgroundColor, .post-slider]
  const parentSection = container.closest("section, .wp-block-blueprint-grid-item");
  if (!parentSection) return;

  // Look for .BackgroundColor as a sibling or in the parent container
  let backgroundColorEl = parentSection.querySelector(".BackgroundColor");
  
  // If not found, try looking in the immediate parent container
  if (!backgroundColorEl) {
    const innerBlocksContainer = container.parentElement;
    if (innerBlocksContainer && innerBlocksContainer.classList.contains('inner-blocks-container')) {
      backgroundColorEl = innerBlocksContainer.querySelector(".BackgroundColor");
    }
  }
  
  if (!backgroundColorEl) return;

  // Get computed background color
  const computedStyle = window.getComputedStyle(backgroundColorEl);
  const backgroundColor = computedStyle.backgroundColor;

  // Apply to swiper if valid color found
  if (backgroundColor && backgroundColor !== "rgba(0, 0, 0, 0)" && backgroundColor !== "transparent") {
    swiperNode.style.setProperty("--swiper-background-color", backgroundColor);
  }
}

function initPostSliders() {
  const containers = document.querySelectorAll(".wp-block-blueprint-post-slider");

  for (let i = 0; i < containers.length; i++) {
    const container = containers[i];
    const swiperNode = container.querySelector(".swiper");
    const paginationEl = container.querySelector(".custom-term-pagination");
    if (!container.childElementCount || !swiperNode || !paginationEl) continue;

    // Copy background color from .BackgroundColor to .swiper
    applySwiperBackgroundColor(container, swiperNode);

    // Read autoscroll settings from data attributes
    const autoscrollEnabled = container.dataset.autoscroll === "true";
    const autoscrollSpeed = parseInt(container.dataset.autoscrollSpeed, 10) || 4000;

    // Collect ALL navigation buttons (desktop + mobile) inside this container
    const allNextButtons = container.querySelectorAll(".swiper-button-next");
    const allPrevButtons = container.querySelectorAll(".swiper-button-prev");

    // Use one element for Swiper's built-in navigation (your Swiper build expects single elements)
    const primaryNextEl = allNextButtons[0] || null;
    const primaryPrevEl = allPrevButtons[0] || null;

    // Init Swiper
    const slider = new Swiper(swiperNode, {
      navigation: { nextEl: primaryNextEl, prevEl: primaryPrevEl },
      slideToClickedSlide: true,
      speed: 1000,
      loop: true,
      slidesPerView: 1.2,
      spaceBetween: 28,
      effect: "slide",
      cssMode: false,
      autoplay: autoscrollEnabled
        ? { delay: autoscrollSpeed, disableOnInteraction: false, pauseOnMouseEnter: true }
        : false,
      // Spring-like easing effect
      on: {
        setTransition: function (swiper, duration) {
          swiper.wrapperEl.style.transitionTimingFunction = "cubic-bezier(0.34, 1.2, 0.64, 1)";
        },
      },
      breakpoints: {
        768: { slidesPerView: 2.64, spaceBetween: 28, centeredSlides: true },
        992: { slidesPerView: 2.64, spaceBetween: 52, centeredSlides: true },
      },
    });

    // Make ALL buttons (desktop + mobile) control this slider
    allNextButtons.forEach(btn =>
      btn.addEventListener("click", e => {
        e.preventDefault();
        slider.slideNext();
        if (autoscrollEnabled) slider.autoplay.start();
      })
    );
    allPrevButtons.forEach(btn =>
      btn.addEventListener("click", e => {
        e.preventDefault();
        slider.slidePrev();
        if (autoscrollEnabled) slider.autoplay.start();
      })
    );

    // ----- AUTOSCROLL PAUSE / PLAY BUTTON -----
    if (autoscrollEnabled) {
      const toggleWrappers = Array.from(container.querySelectorAll(".slider-play-toggle-button"));
      const toggleBtns = toggleWrappers.map(w => w.querySelector("button")).filter(Boolean);

      function setPlaying(playing) {
        toggleWrappers.forEach(w => w.classList.toggle("is-paused", !playing));
        toggleBtns.forEach(btn => {
          btn.setAttribute("aria-label", playing ? "Pause autoplay" : "Play autoplay");
        });
        if (playing) {
          slider.autoplay.start();
        } else {
          slider.autoplay.stop();
        }
      }

      toggleBtns.forEach(btn => {
        btn.addEventListener("click", () => {
          const wrapper = btn.closest(".slider-play-toggle-button");
          const currentlyPaused = wrapper ? wrapper.classList.contains("is-paused") : false;
          setPlaying(currentlyPaused);
        });
      });

      // Keep button state in sync when Swiper pauses/resumes on its own (e.g. on hover)
      slider.on("autoplayStop", () => {
        toggleWrappers.forEach(w => w.classList.add("is-paused"));
        toggleBtns.forEach(btn => btn.setAttribute("aria-label", "Play autoplay"));
      });
      slider.on("autoplayStart", () => {
        toggleWrappers.forEach(w => w.classList.remove("is-paused"));
        toggleBtns.forEach(btn => btn.setAttribute("aria-label", "Pause autoplay"));
      });
    }

    // ----- TERM (DECADE) PAGINATION -----
    // Expect each bullet to represent a DECADE with:
    //  - data-slide-index: index of the FIRST slide in that decade
    //  - optional data-label: decade label like "1940s"
    const bullets = Array.from(
      paginationEl.querySelectorAll(".swiper-pagination-bullet")
    );

    // Build term map sorted by first slide start index
    const termData = bullets
      .map((b, domIdx) => ({
        startIndex: parseInt(b.dataset.slideIndex, 10), // first slide index of that decade
        label: (b.dataset.label || b.textContent || "").trim(),
        bulletIndex: domIdx, // keep DOM position
      }))
      .sort((a, z) => a.startIndex - z.startIndex);

    // Helper: map a slide index -> which term (decade) it belongs to
    function getTermIndexForSlide(slideIndex) {
      for (let i = termData.length - 1; i >= 0; i--) {
        if (slideIndex >= termData[i].startIndex) return i;
      }
      return 0;
    }

    // Clicking a DECADE bullet should jump to the FIRST slide of that decade
    bullets.forEach((bullet) => {
      bullet.addEventListener("click", function () {
        const decadeStart = parseInt(this.dataset.slideIndex, 10);
        if (!Number.isNaN(decadeStart)) {
          if (typeof slider.slideToLoop === "function") slider.slideToLoop(decadeStart);
          else slider.slideTo(decadeStart);
        }
      });
    });

    // ----- TIMELINE + FILLED BULLETS (cumulative) -----
    // track = faint full line; fill = active fill up to active decade
    const track = document.createElement("span");
    track.className = "timeline-track";
    const fill = document.createElement("span");
    fill.className = "timeline-fill";
    paginationEl.prepend(fill);
    paginationEl.prepend(track);

    function setActiveBulletsByTerm(activeTermIdx) {
      // Fill all decades up to and including the active one
      bullets.forEach((b, i) => {
        const isCurrent = i === activeTermIdx;
        const isFilled = i <= activeTermIdx;
        b.classList.toggle("swiper-pagination-bullet-active", isCurrent);
        b.classList.toggle("swiper-pagination-bullet-filled", isFilled);
      });
    }

    function renderLines(activeTermIdx) {
      if (!bullets.length) return;

      const wrap = paginationEl.getBoundingClientRect();
      const first = bullets[0].getBoundingClientRect();
      const last = bullets[bullets.length - 1].getBoundingClientRect();

      const startX = first.left + first.width / 2 - wrap.left;
      const endX = last.left + last.width / 2 - wrap.left;

      track.style.left = `${Math.min(startX, endX)}px`;
      track.style.width = `${Math.abs(endX - startX)}px`;

      // Calculate interpolated fill position based on current slide within decade
      const currentSlide = slider.realIndex ?? slider.activeIndex;
      const currentDecade = termData[activeTermIdx];
      const nextDecade = termData[activeTermIdx + 1];
      
      // Get bullet positions
      const currentBullet = bullets[currentDecade.bulletIndex];
      const currentBulletRect = currentBullet.getBoundingClientRect();
      const currentBulletX = currentBulletRect.left + currentBulletRect.width / 2 - wrap.left;
      
      let targetX = currentBulletX;
      
      // If there's a next decade, interpolate between current and next
      if (nextDecade) {
        const nextBullet = bullets[nextDecade.bulletIndex];
        const nextBulletRect = nextBullet.getBoundingClientRect();
        const nextBulletX = nextBulletRect.left + nextBulletRect.width / 2 - wrap.left;
        
        // Calculate how many slides in this decade
        const slidesInDecade = nextDecade.startIndex - currentDecade.startIndex;
        const slideProgress = currentSlide - currentDecade.startIndex;
        const progressRatio = slideProgress / slidesInDecade;
        
        // Interpolate between current and next bullet position
        targetX = currentBulletX + (nextBulletX - currentBulletX) * progressRatio;
      } else {
        // Last decade - use current bullet position
        targetX = currentBulletX;
      }

      fill.style.left = `${Math.min(startX, targetX)}px`;
      fill.style.width = `${Math.abs(targetX - startX)}px`;
    }

    // ----- MOBILE CONTROLS LABELS (first / current / last decade) -----
    function initMobileControls() {
      const controls = container.querySelector(".slider-controls-mobile");
      if (!controls) return;
      const firstYearEl = controls.querySelector(".first-year");
      const currentYearEl = controls.querySelector(".current-year");
      const lastYearEl = controls.querySelector(".last-year");

      const labelsByDom = bullets.map(b => (b.dataset.label || b.textContent || "").trim());
      if (firstYearEl) firstYearEl.textContent = labelsByDom[0] || "";
      if (lastYearEl) lastYearEl.textContent = labelsByDom[labelsByDom.length - 1] || "";

      function updateCurrentLabel(termIdx) {
        if (currentYearEl) currentYearEl.textContent = labelsByDom[termIdx] || "";
      }

      // Expose for sync below
      return { updateCurrentLabel };
    }

    const mobile = initMobileControls();

    // ----- SYNC: update by REAL slide index (loop-safe) -----
    let lastRealIndex = -1;
    function syncFromReal() {
      const real = slider.realIndex ?? slider.activeIndex;
      if (real === lastRealIndex) return;
      lastRealIndex = real;

      const activeTermIdx = getTermIndexForSlide(real);
      setActiveBulletsByTerm(activeTermIdx);
      requestAnimationFrame(() => renderLines(activeTermIdx)); // measure after layout
      if (mobile) mobile.updateCurrentLabel(activeTermIdx);
    }

    slider.on("afterInit", syncFromReal);
    slider.on("realIndexChange", syncFromReal);
    slider.on("slideChangeTransitionEnd", () => {
      // ensure line position is correct after motion/clones
      requestAnimationFrame(() => {
        const activeTermIdx = getTermIndexForSlide(slider.realIndex ?? slider.activeIndex);
        renderLines(activeTermIdx);
      });
    });
    slider.on("loopFix", () => {
      requestAnimationFrame(() => {
        const activeTermIdx = getTermIndexForSlide(slider.realIndex ?? slider.activeIndex);
        renderLines(activeTermIdx);
      });
    });

    // Initial paint fallback
    requestAnimationFrame(syncFromReal);

    // Resize handling
    window.addEventListener(
      "resize",
      () => requestAnimationFrame(() => {
        const activeTermIdx = getTermIndexForSlide(slider.realIndex ?? slider.activeIndex);
        renderLines(activeTermIdx);
      }),
      { passive: true }
    );
  }
}

document.addEventListener("DOMContentLoaded", initPostSliders);
